import { ReactNode, useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import DisclaimerModal from '../DisclaimerModal/DisclaimerModal';
import PrivacyNoticeModal from '../PrivacyNoticeModal/PrivacyNoticeModal';
import { getPrivacyNotice } from '../../apis/PrivacyNoticeService';
import { getActiveDisclaimer, DisclaimerDto } from '../../apis/DisclaimerService';

interface Props {
  children: ReactNode;
}

const Centered: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex justify-center items-center h-screen">{children}</div>
);

const ProtectedRoute = ({ children }: Props) => {
  const location = useLocation();
  const { inProgress, accounts, instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [attemptedSilent, setAttemptedSilent] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [privacyNoticeRequired, setPrivacyNoticeRequired] = useState<boolean | null>(null);
  const [disclaimerData, setDisclaimerData] = useState<DisclaimerDto | null>(null);
  const [disclaimerRequired, setDisclaimerRequired] = useState<boolean | null>(null);

  // Set active account if missing
  useEffect(() => {
    const active = instance.getActiveAccount();
    if (!active && accounts.length > 0) {
      instance.setActiveAccount(accounts[0]);
    }
  }, [accounts, instance]);

  // Attempt silent SSO once if no accounts yet
  useEffect(() => {
    if (!attemptedSilent && accounts.length === 0 && inProgress === InteractionStatus.None) {
      setAttemptedSilent(true);
      instance
        .ssoSilent({ scopes: ['User.Read'] })
        .catch(() => {})
        .finally(() => setInitializing(false));
    } else if (inProgress === InteractionStatus.None) {
      setInitializing(false);
    }
  }, [attemptedSilent, accounts, inProgress, instance]);

  // Check privacy notice status once authenticated
  useEffect(() => {
    if (!isAuthenticated || initializing) return;

    const controller = new AbortController();
    getPrivacyNotice(controller.signal)
      .then((data) => {
        setPrivacyNoticeRequired(data.requiresAcknowledgment);
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          // 404 = no active notice; other errors = don't block but log
          if (err?.response?.status !== 404) {
            console.warn('[PrivacyNotice] Failed to fetch status:', err);
          }
          setPrivacyNoticeRequired(false);
        }
      });

    return () => controller.abort();
  }, [isAuthenticated, initializing]);

  // Check active disclaimer status once authenticated
  useEffect(() => {
    if (!isAuthenticated || initializing) return;

    const controller = new AbortController();
    getActiveDisclaimer(controller.signal)
      .then((data) => {
        setDisclaimerData(data);
        setDisclaimerRequired(data.requiresAcknowledgment && !data.acknowledgedAt);
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          if (err?.response?.status !== 404) {
            console.warn('[Disclaimer] Failed to fetch active disclaimer:', err);
          }
          setDisclaimerRequired(false);
        }
      });

    return () => controller.abort();
  }, [isAuthenticated, initializing]);

  const handlePrivacyAcknowledged = useCallback(() => {
    setPrivacyNoticeRequired(false);
  }, []);

  const showSpinner = initializing || inProgress !== InteractionStatus.None || (isAuthenticated && (privacyNoticeRequired === null || disclaimerRequired === null));
  if (showSpinner) {
    return (
      <Centered>
        <Spin size="large" />
      </Centered>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Privacy Notice is the first gate (before Disclaimer)
  if (privacyNoticeRequired) {
    return (
      <PrivacyNoticeModal
        open
        onAcknowledge={handlePrivacyAcknowledged}
      />
    );
  }

  return (
    <>
      <DisclaimerModal
        open={!!disclaimerRequired}
        disclaimerData={disclaimerData}
        onAcknowledge={() => setDisclaimerRequired(false)}
      />
      {children}
    </>
  );
};

export default ProtectedRoute;
